import type { Asset } from '@he-novation/config/types/asset.types';
import { UploadCreateResponse } from '@he-novation/config/types/responses/upload.responses';

export type UploadFolder = {
    uuid: string;
    name: string;
    shared?: boolean | number;
    path?: string;
};

export type UploadProgress = {
    loaded: number;
    total: number;
    progress: number;
    bytes: number;
    estimated: number;
    rate: number;
};

export type UploadCallback = (
    uploadGroup: {
        uuid: string;
        name: string;
    },
    file: File,
    folder: UploadFolder
) => void;
export type ProgressCallback = (e: UploadProgress, upload: Upload, cancel?: () => void) => void;
export type ErrorCallback = (
    uploadGroup: {
        uuid: string;
        name: string;
    },
    file: File,
    folder: UploadFolder,
    e: Error
) => void;

export type UploadCallbacks = {
    onUploadProgress?: ProgressCallback;
};

type BaseUpload = {
    file: File;
    uploadGroup: {
        uuid: string;
        name: string;
    };
    uploadIndex: number;
    uploadsTotal: number;
};
export type UploadProgression = {
    debounce?: boolean;
    bitrate: number;
    lastTick: number;
    loaded: number;
    progress: number;
    startTime: number;
    total: number;
    remainingMs: number | null;
};

export type Upload = BaseUpload & {
    timeout?: ReturnType<typeof setTimeout>;
    progression: UploadProgression;
};

export type MultipartPart = {
    fileSize: number;
    url: string;
    part: number;
    uploadProgression?: UploadProgression;
    slice: Blob | null;
    ETag?: string;
};

export type MultipartUpload = BaseUpload & {
    debounce?: boolean;
    parts: MultipartPart[];
    assetUuid: string;
    awsUploadId: string;
    bitrate: MultipartUploadBitrate;
    onProgress?: (u: UploadProgression, complete: boolean) => void;
    onComplete?: (a: Asset) => void;
};

export type MultipartUploadBitrate = {
    depth: number;
    refreshDelay: number;
    uploadHistory: {
        loaded: number;
        timestamp: number;
    }[];
};

export type UploadError = BaseUpload & {
    error: Error;
};

export type CreateUploadCallback = (p: PendingUpload) => Promise<
    UploadCreateResponse & {
        onProgress?: (u: UploadProgression, complete: boolean) => void;
    }
>;

export type PendingUpload = {
    file: File;
    uploadGroup: {
        uuid: string;
        name: string;
    };
    uploadIndex: number;
    uploadsTotal: number;
    ghostUuid?: string;
    parentFileUuid?: string;
    onCreate: CreateUploadCallback;
    onComplete?: (a: Asset) => void;
};

export type FinishedUpload = BaseUpload & {
    startedAt: number;
    finishedAt: number;
};

export type UploaderState = {
    uploads: Upload[];
    pending: PendingUpload[];
    finished: FinishedUpload[];
    errors: UploadError[];
};

export type UploadPart = { part: number; url: string; size: number };

export class UnsupportedFileTypeError extends Error {}
